import { html, css, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { dom } from '@fortawesome/fontawesome-svg-core';

import { variables } from '../../variables';
import '../../icons/icon/apt-icon'
import { AuditInfo, Note } from '@aptitude/shared-vanilla';
import { SelectOption } from '../../types/select-option.interface';


@customElement('apt-notes-form')
export class NotesForm extends LitElement {
  @property({ type: Array })
  options = [] as Array<SelectOption>;

  @property({ type: Boolean })
  enableSelect = true;

  @property({ type: Object })
  editNote: Note = {
    note: '',
    opinion: '',
    auditInfo: {} as AuditInfo
  } as Note;

  @property({ type: Object })
  defaultSelectValue = {} as SelectOption;

  @state()
  noteResponse: Note = {
    note: '',
    opinion: '',
    auditInfo: {} as AuditInfo
  } as Note;

  @state()
  showMessage = false;

  static override get styles() {
    return [
      variables,
      css`
        :host  {
          font-size: 14px;
        }

        .group {
          padding:6px;
        }

        .item {
          padding: 4px;
        }

        .text textarea{
          display: block;
          width: 100%;
          padding: 6px 12px;
          font-size: 14px;
          line-height: 1.428571429;
          color: var(--gray-800);
          background-color: var(--white);
          background-image: none;
          border: 1px solid var(--gray-400);
          border-radius: 4px;
          box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075);
          transition: border-color ease-in-out 0.15s, box-shadow ease-in-out 0.15s;
          -webkit-box-sizing: border-box;
          -moz-box-sizing: border-box;
          box-sizing: border-box;
        }

        .button{
          text-align: right;
        }
        .button button {
          padding: 5px 10px;
          font-size: 12px;
          line-height: 1.5;
          border-radius: 3px;
          color: var(--white);
          background-color: var(--primary);
          border-color: var(--primary);
          text-align: center;
          vertical-align: middle;
          cursor: pointer;
          border: 1px solid transparent;
          white-space: nowrap;
        }
        .message {
          color:  var(--red);
          padding: 5px 10px;
        }

      `,
    ];
  }

  override render() {
    return html`
        <div class="group">
          <div class="item">
            <apt-extended-select 
              .options="${this.options}" 
              @selectionChanged="${this.setSelectedOption}"
              .defaultValue="${this.defaultSelectValue ?? nothing}">
            </apt-extended-select>
            ${this.showMessage ? html`<div class="message">Select an opinion</div>` : nothing}
          </div>
          <div class="item text">
            <textarea 
              id="noteText"
              name="note"
              rows="5" 
              autofocus
              .value=${this.editNote.note ? this.editNote.note : ''}></textarea>
          </div>
          <div class="item button">
            <button type="button" 
            @click="${this.handleResponse}">
              Apply
            </button>
          </div>
        </div>
    `;
  }

  setSelectedOption(event: CustomEvent) {
    this.showMessage = false;
    this.noteResponse.opinion = event.detail.option;
  }

  handleResponse() {
    if (this.noteResponse?.opinion) {
      const noteText = this.shadowRoot?.querySelector('#noteText');
      this.noteResponse.note = (noteText as HTMLTextAreaElement)?.value;

      const customEvent = new CustomEvent('noteResponse', {
        composed: true,
        bubbles: true,
        detail: {
          note: this.noteResponse
        }
      });
      this.dispatchEvent(customEvent);
    } else {
      this.showMessage = true;
    }
  }

  protected override firstUpdated(): void {

    // @ts-expect-error The dom.watch() TS definition indicates no parameters for this. However, when
    // you actually dig into the code, you see that it looks at the arguments after it gets into
    // the code and tries to pull some out if there aren't any (it even supplies an empty object
    // for options if you don't supply one yourself).
    //
    // Note: We have to wait for the first update for the shadow DOM to even be created.
    dom.watch({
      autoReplaceSvgRoot: this.shadowRoot,
      observeMutationsRoot: this.shadowRoot,
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'apt-notes-form': NotesForm;
  }
}
